import { eachDayOfInterval, endOfDay, isWithinInterval, startOfDay, startOfToday } from "date-fns";
import { Fragment, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";

import type { Event } from "@/api/bale.zod";

import { useListEventsSuspenseInfinite } from "@/api/bale";
import { coloring } from "@/coloring";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Missing } from "@/components/Missing";
import { Subtitle } from "@/components/Subtitle";
import { TextLoading } from "@/components/TextLoading";
import { ViewDetector } from "@/components/ViewDetector";
import { useCurrentTime } from "@/hooks/useCurrentTime";

import { Calendar } from "./Calendar";
import { EventCard } from "./EventCard";
import { EVENT_COLORS } from "./EventColors";

export function EventsTimeline({ className }: { className?: string }) {
  return (
    <Suspense
      fallback={
        <div className={twMerge("flex flex-col gap-4", className)}>
          <Subtitle>
            <TextLoading />
          </Subtitle>
          <Card className="h-32 w-full max-w-160 opacity-50 animate-pulse" />
          <Card className="h-32 w-full max-w-160 opacity-50 animate-pulse" />
          <Card className="h-32 w-full max-w-160 opacity-50 animate-pulse" />
        </div>
      }
    >
      <EventsTimelineContent />
    </Suspense>
  );
}

function scrollToEvent(event: Event) {
  const eventCard = document.querySelector(`[data-component=EventCard][data-event-id="${event.id}"]`);
  if (!eventCard) return;
  eventCard.scrollIntoView({ behavior: "smooth", block: "start" });
  eventCard.animate(
    [
      { backgroundColor: "color-mix(in srgb, var(--color-attention) 15%, var(--color-white))" },
      { backgroundColor: "var(--color-white)" },
    ],
    { easing: "ease-in", duration: 200 },
  );
}

function EventsTimelineContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const now = useCurrentTime("@minutely");
  const events = useListEventsSuspenseInfinite(
    {},
    {
      query: {
        select: (data) => data.pages.flatMap((page) => page.data.events),
        getNextPageParam: (data) => data.data.page_info.next_cursor,
      },
    },
  );

  const eventsByDay = Array.from(
    Map.groupBy(events.data, ({ event }) =>
      Math.max(startOfDay(event.start_time!).getTime(), startOfToday().getTime()),
    ),
  )
    .toSorted(([a], [b]) => a - b)
    .map(
      ([day, events]) =>
        [
          new Date(day),
          events.toSorted((a, b) => a.event.start_time!.getTime() - b.event.start_time!.getTime()),
        ] as const,
    );

  const eventColoring = coloring(
    events.data.map(({ event }) => event),
    (event) =>
      eachDayOfInterval({ start: event.start_time!, end: event.end_time ?? now }).map((date) => date.getTime()),
  );

  if (events.data.length === 0) {
    return (
      <Card className="col-1 w-full p-2">
        <Missing className="p-8">{t("no-upcoming-events")}</Missing>
      </Card>
    );
  }

  return (
    <>
      {eventsByDay.map(([day, eventsOnDay]) => (
        <Fragment key={day.getTime()}>
          <div className="flex flex-col gap-6 md:col-1">
            <Subtitle className="md:hidden">{t("date", { date: day })}</Subtitle>
            {eventsOnDay.map(({ event, host, response, visibility_reason, current_attendees, pending_invites }) => (
              <EventCard
                key={event.id}
                event={event}
                host={host}
                currentAttendees={current_attendees}
                pendingInvites={pending_invites}
                onClick={() => navigate(`/event/${event.id}`)}
                response={response}
                visibilityReason={visibility_reason}
                color={EVENT_COLORS[eventColoring(event)! % EVENT_COLORS.length]}
              />
            ))}
          </div>
          <div className="col-2 hidden flex-col gap-6 px-4 md:flex">
            <Subtitle>{t("date", { date: day })}</Subtitle>
            <Calendar
              onSelectEvent={scrollToEvent}
              day={day}
              events={events.data
                .map(({ event }) => event)
                .filter((event) =>
                  isWithinInterval(day, {
                    start: startOfDay(event.start_time!),
                    end: endOfDay(event.end_time ?? new Date()),
                  }),
                )}
              coloring={eventColoring}
            />
          </div>
        </Fragment>
      ))}
      {events.hasNextPage && (
        <ViewDetector once onEnterView={() => events.fetchNextPage()} className="flex flex-col items-center p-4">
          <Button type="button" disabled={events.isFetchingNextPage} onClick={() => events.fetchNextPage()}>
            {t("load-more")}
          </Button>
        </ViewDetector>
      )}
    </>
  );
}
